import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, PageHeader, StatusPill, Field, NumberField, SelectField, type DataListColumn } from "@ozpanel/kit";
import { Button, ConfirmDialog, Section, useAuth, useToast } from "@ozpanel/ui";

// The cross-tenant port-approval queue. A tenant requests a specific public port for one of its
// apps; here an admin approves it — CHOOSING how it is served (the method) and, if wanted, a
// different bind address or host port — or denies it. Nothing opens a port: an approval sets the
// row, and the per-node reconcile renders it into a real port later (P2). A conflict (same
// port+bind on a node, one side exclusive) is refused server-side and surfaced here.

interface PortRequest {
  id: number;
  app_id: number;
  tenant_id: number;
  app_name?: string;
  tenant_name?: string;
  proto: string;
  host_port: number;
  host_port_end?: number;
  container_port: number;
  container_port_end?: number;
  method?: string;
  bind_addr?: string;
  vhost_domain?: string;
  purpose?: string;
  suggested_method?: string;
  status: string;
  decided_reason?: string;
}

interface ApproveForm {
  req: PortRequest;
  method: string;
  bind_addr: string;
  host_port: string;
  vhost_domain: string;
  reason: string;
}

// No "revoked": a revoke prunes its row (the server deletes it), so a revoked port never appears
// in the queue — the list only ever holds live, pending, and denied-with-reason requests.
const STATUSES = ["requested", "approved", "denied"];

function portLabel(p: PortRequest): string {
  return `${p.host_port}${p.host_port_end ? `-${p.host_port_end}` : ""}/${p.proto}`;
}

export function PortRequestsScreen() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const [status, setStatus] = React.useState("requested");
  const [approving, setApproving] = React.useState<ApproveForm | null>(null);
  const [denying, setDenying] = React.useState<PortRequest | null>(null);
  const [denyReason, setDenyReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const q = useQuery({
    queryKey: ["port-requests", status],
    queryFn: () => client.apiFetch<{ requests: PortRequest[] | null; count?: number }>(`/app-port-requests?status=${status}`),
    retry: false,
    refetchInterval: 30000,
  });

  const refresh = () => void qc.invalidateQueries({ queryKey: ["port-requests"] });

  const cols: DataListColumn<PortRequest>[] = [
    { key: "tenant", label: "Account", sortable: true, value: (p) => p.tenant_name || "", render: (p) => <span className="text-[12.5px]">{p.tenant_name || `#${p.tenant_id}`}</span> },
    { key: "app", label: "App", sortable: true, value: (p) => p.app_name || "", render: (p) => <span className="font-mono text-[12.5px]">{p.app_name || `#${p.app_id}`}</span> },
    { key: "port", label: "Public port", sortable: true, value: (p) => p.host_port, render: (p) => <span className="font-mono text-[12.5px]">{portLabel(p)}</span> },
    { key: "cport", label: "→ container", render: (p) => <span className="font-mono text-[12px] text-muted">{p.container_port}{p.container_port_end ? `-${p.container_port_end}` : ""}</span> },
    {
      key: "method", label: "Method", render: (p) => (
        <span className="text-[12px]">{p.status === "approved" ? (p.method || "—") + (p.bind_addr ? ` @ ${p.bind_addr}` : "") : p.suggested_method ? `(suggested: ${p.suggested_method})` : "—"}</span>
      ),
    },
    { key: "purpose", label: "Why", render: (p) => <span className="text-[12px] text-muted">{p.purpose || p.decided_reason || ""}</span> },
    {
      key: "status", label: "Status", sortable: true, value: (p) => p.status,
      render: (p) => <StatusPill tone={p.status === "approved" ? "ok" : p.status === "requested" ? "warn" : "bad"}>{p.status}</StatusPill>,
    },
  ];

  const doApprove = () => {
    if (!approving) return;
    const a = approving;
    if ((a.method === "http_vhost" || a.method === "caddy_sni") && !a.vhost_domain.trim()) {
      notify({ kind: "error", title: "A hostname is required", body: `${a.method} serves a port under a hostname — fill in the hostname.` });
      return;
    }
    setBusy(true);
    void client
      .apiFetch(`/app-port-requests/${a.req.id}/approve`, {
        method: "POST",
        body: JSON.stringify({
          method: a.method,
          bind_addr: a.bind_addr.trim(),
          host_port: Number(a.host_port) || 0,
          vhost_domain: a.vhost_domain.trim(),
          reason: a.reason.trim(),
        }),
      })
      .then(() => {
        notify({ kind: "success", title: "Port approved", body: `${portLabel(a.req)} via ${a.method}` });
        setApproving(null);
        refresh();
      })
      // A conflict (409) comes back as an Error whose message is "port conflict: …" — show it verbatim
      // so the admin can pick another port, bind or method rather than guess.
      .catch((e: unknown) => notify({ kind: "error", title: "Could not approve", body: e instanceof Error ? e.message : String(e) }))
      .finally(() => setBusy(false));
  };

  const doDeny = () => {
    if (!denying) return;
    const d = denying;
    setBusy(true);
    void client
      .apiFetch(`/app-port-requests/${d.id}/deny`, { method: "POST", body: JSON.stringify({ reason: denyReason.trim() }) })
      .then(() => {
        notify({ kind: "success", title: d.status === "approved" ? "Port revoked" : "Request denied" });
        setDenying(null);
        setDenyReason("");
        refresh();
      })
      .catch((e: unknown) => notify({ kind: "error", title: "Could not deny", body: e instanceof Error ? e.message : String(e) }))
      .finally(() => setBusy(false));
  };

  return (
    <div>
      <PageHeader
        title="Port requests"
        subtitle="Tenants ask to open a public port; you approve it and choose how it is served, or deny it"
        actions={
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-line2 bg-bg px-2 py-1 text-[12px] text-fg">
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        }
      />
      <Section title={`${status} requests`} hint={q.data?.count != null ? `${q.data.count}` : undefined} bodyClassName="p-0">
        <div className="p-4">
          <DataList
            items={q.data?.requests ?? []}
            columns={cols}
            getRowKey={(p) => String(p.id)}
            loading={q.isLoading}
            error={q.error}
            errorContext="the port requests"
            emptyMessage={status === "requested" ? "Nothing awaiting a decision." : `No ${status} ports.`}
            rowActions={(p) =>
              p.status === "requested" ? (
                <span className="flex gap-1.5">
                  <Button
                    size="sm"
                    onClick={() =>
                      setApproving({
                        req: p,
                        method: p.suggested_method || (p.vhost_domain ? "caddy_sni" : "netavark_dnat"),
                        bind_addr: "",
                        host_port: String(p.host_port),
                        vhost_domain: p.vhost_domain || "",
                        reason: "",
                      })
                    }
                  >
                    Approve
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setDenying(p); setDenyReason(""); }}>Deny</Button>
                </span>
              ) : p.status === "approved" ? (
                <Button size="sm" variant="ghost" onClick={() => { setDenying(p); setDenyReason(""); }}>Revoke</Button>
              ) : null
            }
          />
        </div>
      </Section>

      {/* APPROVE — the admin CHOOSES the method + bind. sni/http_vhost share a listener (e.g. :443);
          l4/dnat take the host port exclusively — the conflict check enforces that. */}
      <ConfirmDialog
        open={!!approving}
        onOpenChange={(o) => { if (!o) setApproving(null); }}
        title={approving ? `Approve ${portLabel(approving.req)} for ${approving.req.app_name || ""}` : ""}
        confirmLabel="Approve"
        pending={busy}
        onConfirm={doApprove}
        body={
          approving && (
            <div className="space-y-3">
              <SelectField
                label="How it is served (the method)"
                value={approving.method}
                onChange={(v) => setApproving({ ...approving, method: v })}
                options={[
                  { value: "http_vhost", label: "HTTP vhost", help: "An ordinary HTTPS site — shares :443 with other sites." },
                  { value: "caddy_sni", label: "Caddy SNI / TLS passthrough", help: "Shares a TLS listener by SNI; TLS terminates in the container." },
                  { value: "caddy_l4", label: "Caddy layer-4 proxy", help: "Raw TCP/UDP proxy — takes the host port EXCLUSIVELY." },
                  { value: "netavark_dnat", label: "netavark DNAT (kernel)", help: "Kernel forwards the port straight to the container — takes it EXCLUSIVELY." },
                ]}
              />
              <NumberField label="Host port (override the requested one, optional)" value={approving.host_port} onChange={(v) => setApproving({ ...approving, host_port: v })} placeholder={String(approving.req.host_port)} />
              <Field label="Bind address" mono value={approving.bind_addr} onChange={(v) => setApproving({ ...approving, bind_addr: v })} placeholder="blank = the node's public IP" />
              <Field label="Hostname (required for http_vhost / caddy_sni)" mono value={approving.vhost_domain} onChange={(v) => setApproving({ ...approving, vhost_domain: v })} placeholder="e.g. sip.example.com" />
              <label className="block text-[12px] text-muted">
                Note (optional)
                <textarea value={approving.reason} onChange={(e) => setApproving({ ...approving, reason: e.target.value })} className="mt-1 h-14 w-full rounded-lg border border-line2 bg-bg p-2 text-[11.5px] outline-none focus:border-accent" />
              </label>
              {approving.req.purpose && <p className="text-[11.5px] text-muted">Tenant's reason: {approving.req.purpose}</p>}
              <p className="text-[11.5px] leading-relaxed text-muted">Approving records the decision; the port opens when the node reconciles (P2). A conflict is refused.</p>
            </div>
          )
        }
      />

      {/* DENY / REVOKE — a reason the tenant will see. */}
      <ConfirmDialog
        open={!!denying}
        onOpenChange={(o) => { if (!o) { setDenying(null); setDenyReason(""); } }}
        title={denying ? (denying.status === "approved" ? `Revoke ${portLabel(denying)}` : `Deny ${portLabel(denying)}`) : ""}
        confirmLabel={denying?.status === "approved" ? "Revoke" : "Deny"}
        destructive
        pending={busy}
        onConfirm={doDeny}
        body={
          <label className="block text-[12px] text-muted">
            Reason (shown to the tenant)
            <textarea value={denyReason} onChange={(e) => setDenyReason(e.target.value)} className="mt-1 h-16 w-full rounded-lg border border-line2 bg-bg p-2 text-[11.5px] outline-none focus:border-accent" />
          </label>
        }
      />
    </div>
  );
}
